import React, { useState } from 'react';
import { Plus, Trash2, GripVertical, X } from 'lucide-react';

const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'long_text', label: 'Long Text' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'dropdown', label: 'Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'radio', label: 'Radio' }
];

const FormFieldsBuilder = ({ fields = [], onChange, onContentChange }) => {
    const [expandedOptions, setExpandedOptions] = useState({});

    const generateFieldId = () => `field_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const addField = () => {
        const newField = {
            id: generateFieldId(),
            type: 'text',
            label: '',
            placeholder: '',
            required: false,
            options: []
        };
        const updated = [...fields, newField];
        onChange(updated);
        onContentChange?.('form_fields', 'Added new field');
    };

    const updateField = (index, key, value) => {
        const updated = [...fields];
        updated[index] = { ...updated[index], [key]: value };
        // When switching to a type that needs options, initialize empty options
        if (key === 'type' && ['dropdown', 'checkbox', 'radio'].includes(value)) {
            if (!updated[index].options || updated[index].options.length === 0) {
                updated[index].options = [''];
                setExpandedOptions(prev => ({ ...prev, [index]: true }));
            }
        }
        onChange(updated);
    };

    const removeField = (index) => {
        const fieldLabel = fields[index]?.label || `Field ${index + 1}`;
        const updated = fields.filter((_, i) => i !== index);
        onChange(updated);
        onContentChange?.('form_fields', `Removed field: ${fieldLabel}`);
    };

    const moveField = (index, direction) => {
        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex >= 0 && newIndex < fields.length) {
            const updated = [...fields];
            [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
            onChange(updated);
        }
    };

    // Options management for dropdown/checkbox/radio
    const addOption = (fieldIndex) => {
        const updated = [...fields];
        const options = [...(updated[fieldIndex].options || []), ''];
        updated[fieldIndex] = { ...updated[fieldIndex], options };
        onChange(updated);
    };

    const updateOption = (fieldIndex, optIndex, value) => {
        const updated = [...fields];
        const options = [...(updated[fieldIndex].options || [])];
        options[optIndex] = value;
        updated[fieldIndex] = { ...updated[fieldIndex], options };
        onChange(updated);
    };

    const removeOption = (fieldIndex, optIndex) => {
        const updated = [...fields];
        const options = (updated[fieldIndex].options || []).filter((_, i) => i !== optIndex);
        updated[fieldIndex] = { ...updated[fieldIndex], options };
        onChange(updated);
    };

    const hasOptions = (type) => ['dropdown', 'checkbox', 'radio'].includes(type);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-gray-800 dark:text-slate-100">Form Fields</h4>
                <button
                    type="button"
                    onClick={addField}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/5 border border-primary/30 rounded-lg transition-colors shrink-0"
                >
                    <Plus className="w-4 h-4" />
                    Add Field
                </button>
            </div>

            {/* Field List */}
            {fields.length === 0 ? (
                <div className="bg-gray-50 dark:bg-slate-800/50 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg p-8 text-center">
                    <p className="text-sm text-gray-500 dark:text-slate-400 mb-1">No form fields yet</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500">Click "Add Field" to get started</p>
                </div>
            ) : (
                <div className="space-y-2">
                    {fields.map((field, index) => (
                        <div key={field.id} className="space-y-0">
                            {/* Main Row */}
                            <div className="flex items-center gap-2 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-2.5">
                                {/* Drag Handle */}
                                <div
                                    className="flex flex-col gap-0 cursor-grab text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300 flex-shrink-0"
                                    title="Drag to reorder"
                                >
                                    <button type="button" onClick={() => moveField(index, 'up')} disabled={index === 0} className="p-0 disabled:opacity-20">
                                        <GripVertical className="w-4 h-4" />
                                    </button>
                                </div>

                                {/* Label Input */}
                                <input
                                    type="text"
                                    value={field.label}
                                    onChange={(e) => updateField(index, 'label', e.target.value)}
                                    placeholder="Field label"
                                    className="flex-1 min-w-0 px-3 py-1.5 border border-gray-200 dark:border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-primary focus:border-transparent bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500"
                                />

                                {/* Delete */}
                                <button
                                    type="button"
                                    onClick={() => removeField(index)}
                                    className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors flex-shrink-0"
                                    title="Remove field"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Type + Required Row */}
                            <div className="flex flex-wrap items-center gap-3 px-3 py-2 ml-6">
                                {/* Type Dropdown */}
                                <select
                                    value={field.type}
                                    onChange={(e) => updateField(index, 'type', e.target.value)}
                                    className="px-3 py-1.5 border border-gray-200 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-800 text-gray-800 dark:text-slate-200 focus:ring-2 focus:ring-primary focus:border-transparent"
                                >
                                    {FIELD_TYPES.map(ft => (
                                        <option key={ft.value} value={ft.value}>{ft.label}</option>
                                    ))}
                                </select>

                                {/* Required Toggle */}
                                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                                    <div className="relative">
                                        <input
                                            type="checkbox"
                                            checked={field.required}
                                            onChange={(e) => updateField(index, 'required', e.target.checked)}
                                            className="sr-only peer"
                                        />
                                        <div className="w-9 h-5 bg-gray-200 dark:bg-slate-700 peer-focus:ring-2 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary"></div>
                                    </div>
                                    <span className="text-sm text-gray-600 dark:text-slate-300 font-medium">Required</span>
                                </label>

                                {/* Expand options toggle for dropdown/checkbox/radio */}
                                {hasOptions(field.type) && (
                                    <button
                                        type="button"
                                        onClick={() => setExpandedOptions(prev => ({ ...prev, [index]: !prev[index] }))}
                                        className="text-xs text-primary hover:text-primary-hover font-medium"
                                    >
                                        {expandedOptions[index] ? 'Hide options' : 'Edit options'}
                                    </button>
                                )}
                            </div>

                            {/* Options Editor (for dropdown/checkbox/radio) */}
                            {hasOptions(field.type) && expandedOptions[index] && (
                                <div className="ml-6 mr-3 mb-2 p-3 bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700 rounded-lg space-y-2">
                                    <p className="text-xs font-medium text-gray-500 dark:text-slate-400">Options</p>
                                    {(field.options || []).map((option, optIndex) => (
                                        <div key={optIndex} className="flex items-center gap-2">
                                            <span className="text-xs text-gray-400 dark:text-slate-500 w-5 text-right">{optIndex + 1}.</span>
                                            <input
                                                type="text"
                                                value={option}
                                                onChange={(e) => updateOption(index, optIndex, e.target.value)}
                                                placeholder={`Option ${optIndex + 1}`}
                                                className="flex-1 px-2.5 py-1.5 border border-gray-200 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-900 text-gray-800 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeOption(index, optIndex)}
                                                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                                            >
                                                <X className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => addOption(index)}
                                        className="flex items-center gap-1 text-xs text-primary hover:text-primary-hover font-medium mt-1"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        Add option
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Summary */}
            {fields.length > 0 && (
                <div className="flex items-center justify-between text-xs text-gray-500 dark:text-slate-400 px-1">
                    <span>{fields.length} field{fields.length !== 1 ? 's' : ''}</span>
                    <span>{fields.filter(f => f.required).length} required</span>
                </div>
            )}
        </div>
    );
};

export default FormFieldsBuilder;
